import { FC, PropsWithChildren } from 'react'
import { BusinessRole } from '../../types/api/user'
import { useHasRole } from '../../hooks/use-has-role'

interface RoleGuardProps {
    /**
     * Роли, которые требуются для отображения содержимого
     */
    requiredRoles: BusinessRole | BusinessRole[]
    /**
     * Если true, пользователь должен иметь все указанные роли
     * Если false (по умолчанию), достаточно одной из указанных ролей
     */
    requireAll?: boolean
    /**
     * Что отобразить, если у пользователя нет доступа
     * По умолчанию - ничего (null)
     */
    fallback?: React.ReactNode
}

/**
 * Компонент для условного отображения контента на основе ролей пользователя
 * Используется для защиты отдельных частей UI внутри страниц
 *
 * @example
 * <RoleGuard requiredRoles={BusinessRole.Manager}>
 *   <Button>Только для менеджеров</Button>
 * </RoleGuard>
 *
 * @example
 * <RoleGuard
 *   requiredRoles={[BusinessRole.Manager, BusinessRole.Designer]}
 *   requireAll={true}
 *   fallback={<div>Нет доступа</div>}
 * >
 *   <AdminPanel />
 * </RoleGuard>
 */
const RoleGuard: FC<PropsWithChildren<RoleGuardProps>> = ({
    children,
    requiredRoles,
    requireAll = false,
    fallback = null,
}) => {
    const hasRequiredRole = useHasRole(requiredRoles, requireAll)

    if (!hasRequiredRole) {
        return <>{fallback}</>
    }

    return <>{children}</>
}

export default RoleGuard
